import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import * as React from "react";
import { type DateRange, DayPicker } from "react-day-picker";

import { cn } from "@/v2/lib/utils";
import { buttonVariants } from "./button";

function Calendar({
	className,
	showOutsideDays = true,
	...props
}: React.ComponentProps<typeof DayPicker>) {
	const [hovered, setHovered] = React.useState<Date | undefined>(undefined);
	const rootLeaveCleanupRef = React.useRef<(() => void) | null>(null);

	React.useEffect(() => {
		return () => {
			rootLeaveCleanupRef.current?.();
		};
	}, []);

	// Airbnb-style live range preview: while the end date is still being picked,
	// highlight start -> hovered with a lighter band. Range-only.
	let rangeProps: Partial<React.ComponentProps<typeof DayPicker>> = {};
	if (props.mode === "range") {
		const selected = props.selected as DateRange | undefined;
		const from = selected?.from;
		const picking = Boolean(from && !selected?.to);
		let start: Date | undefined;
		let end: Date | undefined;

		if (picking && hovered && from) {
			start = hovered < from ? hovered : from;
			end = hovered < from ? from : hovered;
		}

		rangeProps = {
			onDayMouseEnter: (day, m, e) => {
				setHovered(day);
				props.onDayMouseEnter?.(day, m, e);
			},
			onDayMouseLeave: (day, m, e) => {
				props.onDayMouseLeave?.(day, m, e);

				// Keep the preview active while moving through gutters between days.
				// Clear it only once the pointer actually leaves the calendar root.
				const root = e.currentTarget.closest(
					"[data-date-picker-calendar-root]",
				);
				if (
					e.relatedTarget instanceof Node &&
					root?.contains(e.relatedTarget)
				) {
					if (!rootLeaveCleanupRef.current) {
						const clearOnRootLeave = () => {
							setHovered(undefined);
							root.removeEventListener("mouseleave", clearOnRootLeave);
							rootLeaveCleanupRef.current = null;
						};

						root.addEventListener("mouseleave", clearOnRootLeave);
						rootLeaveCleanupRef.current = () => {
							root.removeEventListener("mouseleave", clearOnRootLeave);
							rootLeaveCleanupRef.current = null;
						};
					}

					return;
				}

				setHovered(undefined);
				rootLeaveCleanupRef.current?.();
			},
			modifiers: {
				...(props.modifiers ?? {}),
				...(start && end
					? {
							preview: { from: start, to: end },
							preview_hovered: hovered,
							preview_start: start,
							preview_end: end,
						}
					: {}),
			},
			modifiersClassNames: {
				...(props.modifiersClassNames ?? {}),
				preview: "bg-primary/10",
				preview_hovered:
					"[&>button]:hover:bg-transparent! [&>button]:hover:text-foreground!",
				preview_start: "rounded-l-full",
				preview_end: "rounded-r-full",
			},
		};
	}

	const dayPicker = (
		<DayPicker
			showOutsideDays={showOutsideDays}
			data-date-picker-calendar-root="true"
			className={cn("relative mx-auto w-fit px-2 pb-3 pt-2", className)}
			classNames={{
				months: "flex flex-col gap-3",
				month: "flex flex-col gap-3",
				month_caption: "flex h-9 items-center justify-center",
				caption_label: "text-sm font-medium",
				nav: "absolute inset-x-2 top-2 flex h-9 items-center justify-between pointer-events-none",
				button_previous: cn(
					buttonVariants({ variant: "ghost" }),
					"size-9 p-0 pointer-events-auto",
				),
				button_next: cn(
					buttonVariants({ variant: "ghost" }),
					"size-9 p-0 pointer-events-auto",
				),
				month_grid: "w-full border-collapse",
				weekdays: "flex",
				weekday:
					"w-9 select-none text-center text-xs font-normal text-muted-foreground",
				week: "mt-1 flex w-full",
				day: "relative h-9 w-9 p-0 text-center text-sm",
				day_button: cn(
					buttonVariants({ variant: "ghost" }),
					"size-9 rounded-full p-0 font-normal aria-selected:opacity-100",
				),
				selected:
					"[&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:rounded-full [&>button]:hover:bg-primary [&>button]:hover:text-primary-foreground",
				range_start:
					"bg-primary/10 rounded-l-full [&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:rounded-full [&>button]:hover:bg-primary [&>button]:hover:text-primary-foreground",
				range_end:
					"bg-primary/10 rounded-r-full [&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:rounded-full [&>button]:hover:bg-primary [&>button]:hover:text-primary-foreground",
				range_middle:
					"bg-primary/10 rounded-none [&>button]:bg-transparent! [&>button]:text-foreground! [&>button]:rounded-full [&>button]:hover:bg-primary! [&>button]:hover:text-primary-foreground!",
				outside: "text-muted-foreground opacity-50",
				disabled: "text-muted-foreground opacity-50",
				hidden: "invisible",
			}}
			components={{
				Chevron: ({ orientation }) => {
					const Icon =
						orientation === "left" ? ChevronLeftIcon : ChevronRightIcon;
					return <Icon className="size-4" />;
				},
			}}
			{...({ ...props, ...rangeProps } as React.ComponentProps<
				typeof DayPicker
			>)}
		/>
	);

	return dayPicker;
}

Calendar.displayName = "Calendar";

export { Calendar };
